import React, { useCallback, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Button from "shared/components/FormElements/Button";
import LayoutHeader from "./LayoutHeader";
import List from "./List";

import "./Layout.css";
import { isInternationalTheme } from "config/ConfigUtils";

// Check if #newitem hash is present
const hasNewItemHash = () => window.location.hash.toLowerCase() === "#newitem";

const LayoutMasterDetail = ({
  itemType,
  SelectedItemComponent,
  NewItemComponent,
  BulkProcessComponent,
  items,
  listColumns,
  listName,
  standardHelpItems,
  highlightedHelpItems,
  DeleteItem,
  InsertItem,
  UpdateItem,
  isAlwaysFullScreen,
  hideCreateNewItem,
  hideCreateNewItemWhenSelected,
  showImportSpreadsheet,
  showSelectedYear,
  isNewItem,
  isBulkProcess,
  hideHeader = false,
  blockerConditions = [],
  getRowClassName,
  // Opt-in: true ise liste yüklendiğinde İLK öğeyi bir kez otomatik açar.
  // Default false → diğer tüm listeler (28 kullanım) etkilenmez.
  autoOpenFirstItem = false,
}) => {
  const { t } = useTranslation();

  // Read #newitem hash once on mount
  const isNewItemFromHash = useRef(hasNewItemHash());

  // Consume and clear hash after reading
  useEffect(() => {
    if (isNewItemFromHash.current) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }, []);

  const blocker = blockerConditions.find((b) => b?.condition === true);

  const [selectedItemId, setSelectedItemId] = useState();
  const [selectedItemShow, setSelectedItemShow] = useState(false);
  const [newItemShow, setNewItemShow] = useState(isNewItem || isNewItemFromHash.current);
  const [bulkProcessShow, setBulkProcessShow] = useState(isBulkProcess ? true : false);
  const [isFullScreen, setIsFullScreen] = useState(isAlwaysFullScreen ? true : false);
  const [isMobile, setIsMobile] = useState(true);

  const handleWindowSizeChange = useCallback(() => {
    if (window.innerWidth <= 1156) {
      setIsFullScreen(true);
      setIsMobile(true);
    } else {
      if (!isAlwaysFullScreen) {
        setIsFullScreen(false);
      }
      setIsMobile(false);
    }
  }, [isAlwaysFullScreen]);

  useEffect(() => {
    handleWindowSizeChange();
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, [handleWindowSizeChange]);

  const ItemSelectHandler = useCallback(
    (itemid) => {
      setSelectedItemShow(true);
      setNewItemShow(false);
      setBulkProcessShow(false);
      setSelectedItemId(itemid);
      if (!isMobile && !isAlwaysFullScreen) {
        setIsFullScreen(false);
      }
    },
    [isMobile, isAlwaysFullScreen]
  );

  // Opt-in otomatik açılış: liste dolduğunda İLK öğeyi BİR KEZ aç.
  // Guard'lar: yalnızca autoOpenFirstItem true iken, #newitem modunda değilken,
  // henüz seçim yokken ve bir kez (ref ile kilitli). Veri async geldiği için items izlenir.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!autoOpenFirstItem) return;
    if (autoOpenedRef.current) return;
    if (isNewItemFromHash.current || newItemShow) return;
    if (selectedItemId) return;
    if (!Array.isArray(items) || items.length === 0) return;
    const firstId = items[0]?.id ?? items[0]?._id;
    if (!firstId) return;
    autoOpenedRef.current = true;
    ItemSelectHandler(firstId);
  }, [autoOpenFirstItem, items, selectedItemId, newItemShow, ItemSelectHandler]);

  const newItemHandler = useCallback(() => {
    setSelectedItemShow(false);
    setNewItemShow(true);
    setBulkProcessShow(false);
    setSelectedItemId(null);
  }, []);

  const bulkProcessHandler = useCallback(() => {
    setSelectedItemShow(false);
    setNewItemShow(false);
    setBulkProcessShow(true);
    setSelectedItemId(null);
  }, []);

  const closeRightItemHandler = useCallback(() => {
    setNewItemShow(false);
    setSelectedItemShow(false);
    setBulkProcessShow(false);
    setSelectedItemId(null);
  }, []);

  const isHeaderHidden = hideCreateNewItem && !standardHelpItems && !highlightedHelpItems && !showSelectedYear;

  const showHeader =
    !hideCreateNewItem || standardHelpItems || highlightedHelpItems || (showSelectedYear && !selectedItemId);

  const isRightSideOpen = selectedItemShow || newItemShow || bulkProcessShow;
  return (
    <React.Fragment>
      {!hideHeader && ((showHeader && !blocker) || selectedItemId) && (
        <LayoutHeader
          itemType={itemType}
          selectedItemId={selectedItemId}
          isNewItem={newItemShow}
          isBulkProcess={bulkProcessShow}
          onNewItem={newItemHandler}
          onBulkProcess={bulkProcessHandler}
          showSelectedYear={showSelectedYear && !isRightSideOpen}
          hideCreateNewItem={hideCreateNewItem}
          hideCreateNewItemWhenSelected={hideCreateNewItemWhenSelected}
          showImportSpreadsheet={showImportSpreadsheet}
          standardHelpItems={standardHelpItems}
          highlightedHelpItems={highlightedHelpItems}
          isFullScreen={isFullScreen}
          onClose={closeRightItemHandler}
          onFullScreen={() => setIsFullScreen(true)}
          onHalfScreen={() => setIsFullScreen(false)}
          isMobile={isMobile}
          isAlwaysFullScreen={isAlwaysFullScreen}
          //hasBulkProcessComponent={!!BulkProcessComponent && process.env.REACT_APP_SHOW_BULK_PROCESS === "1"}
          hasBulkProcessComponent={!!BulkProcessComponent && isInternationalTheme()}
        />
      )}
      <div className="layout-container">
        {blocker && (
          <div className="layout-blocker-container">
            <div style={{ marginBottom: "20px" }}>{t(blocker.message)}</div>
            {blocker.redirectTo && <Button to={blocker.redirectTo}>{t(blocker.cta)}</Button>}
          </div>
        )}
        {items && !blocker && (
          <div
            className={isHeaderHidden ? "layout-list-container-without-header" : "layout-list-container"}
            style={{ display: isFullScreen && (selectedItemShow || newItemShow || bulkProcessShow) ? "none" : "flex" }}
          >
            <List
              name={listName || itemType}
              items={items}
              columns={listColumns}
              onItemSelect={ItemSelectHandler}
              isVisible={!(isFullScreen && (selectedItemShow || newItemShow || bulkProcessShow))}
              getRowClassName={getRowClassName}
            />
          </div>
        )}

        {((selectedItemId && selectedItemShow) || newItemShow || bulkProcessShow) && (
          <div className={"layout-selected-item-container"}>
            {selectedItemId && selectedItemShow && (
              <React.Fragment>
                <SelectedItemComponent
                  key={selectedItemId}
                  itemId={selectedItemId}
                  onClose={closeRightItemHandler}
                  DeleteItem={DeleteItem}
                  UpdateItem={UpdateItem}
                  InsertItem={InsertItem}
                  gotoItem={ItemSelectHandler}
                  itemType={itemType}
                ></SelectedItemComponent>
              </React.Fragment>
            )}
            {newItemShow && (
              <div>
                <div
                  style={{
                    fontWeight: "bold",
                    marginBottom: "20px",
                    borderBottom: "1px solid gray",
                    paddingBottom: "4px",
                    width: "max-content",
                    marginTop: "10px",
                  }}
                >
                  {t(`${itemType}.createNewItem`)}
                </div>
                <NewItemComponent
                  isNewItem={true}
                  gotoItem={ItemSelectHandler}
                  onClose={closeRightItemHandler}
                  InsertItem={InsertItem}
                ></NewItemComponent>
              </div>
            )}
            {bulkProcessShow && (
              <BulkProcessComponent isBulkProcess={true} onClose={closeRightItemHandler}></BulkProcessComponent>
            )}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default LayoutMasterDetail;
